import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Mail, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { User } from '@supabase/supabase-js';

interface EmailPreferencesCardProps {
  user: User;
  isClubMember: boolean;
}

// The Club's weekly resource email is shown to Club members only (30 Oct 2026); the setting itself is unchanged.
export function EmailPreferencesCard({ user, isClubMember }: EmailPreferencesCardProps) {
  const [resourceUpdates, setResourceUpdates] = useState(true);
  const [promotionalEmails, setPromotionalEmails] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase
        .from('email_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (data) {
        setResourceUpdates(data.resource_updates);
        setPromotionalEmails(data.promotional_emails);
      }
      setIsLoading(false);
    };
    fetch();
  }, [user.id]);

  const handleToggle = async (field: 'resource_updates' | 'promotional_emails', value: boolean) => {
    if (field === 'resource_updates') setResourceUpdates(value);
    else setPromotionalEmails(value);

    const { error } = await supabase
      .from('email_preferences')
      .update({ [field]: value })
      .eq('user_id', user.id);

    if (error) {
      // Revert
      if (field === 'resource_updates') setResourceUpdates(!value);
      else setPromotionalEmails(!value);
      toast({ title: 'Error', description: 'Failed to update preference.', variant: 'destructive' });
    }
  };

  if (isLoading) {
    return (
      <Card className="rounded-none border-0 border-t border-[var(--sn-border)] bg-transparent px-0 py-6 shadow-none flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </Card>
    );
  }

  return (
    <Card className="rounded-none border-0 border-t border-[var(--sn-border)] bg-transparent px-0 py-6 shadow-none">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Mail className="w-5 h-5 text-primary" />
        </div>
        <h4 className="font-medium text-foreground">Email Preferences</h4>
      </div>
      <div className="space-y-4">
        {isClubMember && (
          <div className="flex items-center justify-between">
            <Label htmlFor="resource-updates" className="text-sm text-muted-foreground cursor-pointer">
              Club resource updates, weekly
            </Label>
            <Switch
              id="resource-updates"
              checked={resourceUpdates}
              onCheckedChange={(v) => handleToggle('resource_updates', v)}
            />
          </div>
        )}
        <div className="flex items-center justify-between">
          <Label htmlFor="promo-emails" className="text-sm text-muted-foreground cursor-pointer">
            Product news and offers
          </Label>
          <Switch
            id="promo-emails"
            checked={promotionalEmails}
            onCheckedChange={(v) => handleToggle('promotional_emails', v)}
          />
        </div>
      </div>
    </Card>
  );
}
